import React, { useEffect, useState, useRef } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Share2, RotateCcw, Check, Copy, User, Download, Facebook, MessageSquare, Twitter, Instagram, Loader2, MessageCircle, Save as SaveIcon, X as XIcon, Upload } from "lucide-react";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";
import { saveLocalLineup, loadLocalLineup, LINEUP_STORAGE_KEY } from "../lib/localLineup";
import { readShirtImageFile } from "../lib/customShirtUpload";
import { toJpeg } from "html-to-image";
import { ShirtIcon } from "../components/ShirtIcon";
import PostToCommentsModal from "../components/PostToCommentsModal";
import PostHelmet from "../components/PostHelmet";
import { toast } from "../components/ui/sonner";

const FORMATIONS = {
  "4-4-2": [
    { role: "GK", name: "", number: 1, x: 50, y: 88, is_gk: true },
    { role: "RB", name: "", number: 2, x: 82, y: 70 },
    { role: "RCB", name: "", number: 5, x: 60, y: 70 },
    { role: "LCB", name: "", number: 6, x: 40, y: 70 },
    { role: "LB", name: "", number: 3, x: 18, y: 70 },
    { role: "RM", name: "", number: 7, x: 82, y: 40 },
    { role: "RCM", name: "", number: 8, x: 60, y: 40 },
    { role: "LCM", name: "", number: 4, x: 40, y: 40 },
    { role: "LM", name: "", number: 11, x: 18, y: 40 },
    { role: "RST", name: "", number: 9, x: 60, y: 15 },
    { role: "LST", name: "", number: 10, x: 40, y: 15 },
  ],
  "4-3-3": [
    { role: "GK", name: "", number: 1, x: 50, y: 88, is_gk: true },
    { role: "RB", name: "", number: 2, x: 82, y: 70 },
    { role: "RCB", name: "", number: 5, x: 60, y: 70 },
    { role: "LCB", name: "", number: 6, x: 40, y: 70 },
    { role: "LB", name: "", number: 3, x: 18, y: 70 },
    { role: "RCM", name: "", number: 8, x: 72, y: 45 },
    { role: "CM", name: "", number: 4, x: 50, y: 45 },
    { role: "LCM", name: "", number: 10, x: 28, y: 45 },
    { role: "RW", name: "", number: 7, x: 80, y: 18 },
    { role: "ST", name: "", number: 9, x: 50, y: 12 },
    { role: "LW", name: "", number: 11, x: 20, y: 18 },
  ],
  "4-2-3-1": [
    { role: "GK", name: "", number: 1, x: 50, y: 88, is_gk: true },
    { role: "RB", name: "", number: 2, x: 82, y: 70 },
    { role: "RCB", name: "", number: 5, x: 60, y: 70 },
    { role: "LCB", name: "", number: 6, x: 40, y: 70 },
    { role: "LB", name: "", number: 3, x: 18, y: 70 },
    { role: "RDM", name: "", number: 4, x: 60, y: 52 },
    { role: "LDM", name: "", number: 8, x: 40, y: 52 },
    { role: "RAM", name: "", number: 7, x: 78, y: 30 },
    { role: "AM", name: "", number: 10, x: 50, y: 30 },
    { role: "LAM", name: "", number: 11, x: 22, y: 30 },
    { role: "ST", name: "", number: 9, x: 50, y: 12 },
  ],
  "3-5-2": [
    { role: "GK", name: "", number: 1, x: 50, y: 88, is_gk: true },
    { role: "RCB", name: "", number: 5, x: 70, y: 72 },
    { role: "CB", name: "", number: 6, x: 50, y: 72 },
    { role: "LCB", name: "", number: 2, x: 30, y: 72 },
    { role: "RWB", name: "", number: 7, x: 85, y: 45 },
    { role: "RCM", name: "", number: 8, x: 63, y: 45 },
    { role: "CM", name: "", number: 4, x: 50, y: 45 },
    { role: "LCM", name: "", number: 10, x: 37, y: 45 },
    { role: "LWB", name: "", number: 3, x: 15, y: 45 },
    { role: "RST", name: "", number: 9, x: 60, y: 15 },
    { role: "LST", name: "", number: 11, x: 40, y: 15 },
  ],
  "5-3-2": [
    { role: "GK", name: "", number: 1, x: 50, y: 88, is_gk: true },
    { role: "RWB", name: "", number: 2, x: 85, y: 72 },
    { role: "RCB", name: "", number: 5, x: 68, y: 72 },
    { role: "CB", name: "", number: 6, x: 50, y: 72 },
    { role: "LCB", name: "", number: 4, x: 32, y: 72 },
    { role: "LWB", name: "", number: 3, x: 15, y: 72 },
    { role: "RCM", name: "", number: 8, x: 70, y: 45 },
    { role: "CM", name: "", number: 14, x: 50, y: 45 },
    { role: "LCM", name: "", number: 10, x: 30, y: 45 },
    { role: "RST", name: "", number: 9, x: 60, y: 15 },
    { role: "LST", name: "", number: 11, x: 40, y: 15 },
  ],
  "3-4-2-1": [
    { role: "GK", name: "", number: 1, x: 50, y: 88, is_gk: true },
    { role: "RCB", name: "", number: 5, x: 65, y: 72 },
    { role: "CB", name: "", number: 6, x: 50, y: 74 },
    { role: "LCB", name: "", number: 4, x: 35, y: 72 },
    { role: "RWB", name: "", number: 2, x: 84, y: 48 },
    { role: "RCM", name: "", number: 8, x: 60, y: 48 },
    { role: "LCM", name: "", number: 10, x: 40, y: 48 },
    { role: "LWB", name: "", number: 3, x: 16, y: 48 },
    { role: "RAM", name: "", number: 7, x: 65, y: 22 },
    { role: "LAM", name: "", number: 11, x: 35, y: 22 },
    { role: "ST", name: "", number: 9, x: 50, y: 10 },
  ],
};

export default function LineupBuilderPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const copy = usePageContent("lineup_builder");

  // Shirt labels show surname only to save space — squad list / dropdown
  // picker still show the full name.
  const getSurname = (fullName) => {
    if (!fullName) return "";
    const parts = fullName.trim().split(/\s+/);
    return parts[parts.length - 1];
  };

  const [formation, setFormation] = useState("4-4-2");
  const [lineupPlayers, setLineupPlayers] = useState([]);
  const [squad, setSquad] = useState([]);
  const [activeSlotIdx, setActiveSlotIdx] = useState(null);
  const [customNameDraft, setCustomNameDraft] = useState("");
  // Team Kit — a single bulk shirt image applied to every outfield slot
  // at once (toolbar control, not per-slot); kept 100% client-side, same
  // as the removed per-slot upload was. Never applies to the GK slot.
  const [teamKitUrl, setTeamKitUrl] = useState("");
  const [teamKitError, setTeamKitError] = useState("");
  
  const [sharing, setSharing] = useState(false);
  const [shareCode, setShareCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [resumeData, setResumeData] = useState(null);

  const [generatedImage, setGeneratedImage] = useState("");
  const [generatingImg, setGeneratingImg] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [shareFile, setShareFile] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);

  const pitchRef = useRef(null);
  const [draggingIdx, setDraggingIdx] = useState(null);

  // Bigger shirts on desktop only — driven by JS (not a pure `lg:` CSS
  // media query) so it can be deterministically forced back to the
  // compact/mobile size while exporting a JPEG, regardless of how wide
  // the real desktop browser window is. See `withCompactShirtsForExport`.
  const [isDesktopView, setIsDesktopView] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [forceCompactShirts, setForceCompactShirts] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktopView(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const shirtSizeClass = isDesktopView && !forceCompactShirts ? "h-20 w-20" : "h-14 w-14";
  // Forces the ACTUAL pitch element's on-screen width down to the mobile
  // export width (see EXPORT_W below) before capture, so the browser's
  // real layout engine reflows the pitch/shirts/labels into the portrait
  // shape — html-to-image then just captures whatever's really on screen,
  // instead of relying on its own (unreliable, for this element's
  // aspect-ratio/max-height combo) width/height-forcing option.
  const [forceExportLayout, setForceExportLayout] = useState(false);
  // Synchronously shrinks shirts + the pitch itself to mobile export size,
  // lets the browser re-lay-out, runs `capture`, then restores everything —
  // used around every toJpeg() call so exports always match the mobile
  // portrait layout on both mobile and desktop.
  const withExportLayout = async (capture) => {
    flushSync(() => {
      setForceCompactShirts(true);
      setForceExportLayout(true);
    });
    try {
      return await capture();
    } finally {
      flushSync(() => {
        setForceCompactShirts(false);
        setForceExportLayout(false);
      });
    }
  };

  // 1. Load Squad list and/or Shared Lineup
  useEffect(() => {
    api.get("/squad").then((r) => setSquad(r.data || [])).catch(() => {});

    if (code) {
      setLoading(true);
      setIsViewOnly(true);
      api.get(`/lineup-builder/${code}`)
        .then((r) => {
          setFormation(r.data.formation);
          setLineupPlayers(r.data.players || []);
        })
        .catch(() => {
          alert("Shared lineup not found. Loading editor.");
          navigate("/lineup-builder");
        })
        .finally(() => setLoading(false));
    } else {
      setIsViewOnly(false);
      // Initialize with default formation
      setLineupPlayers(JSON.parse(JSON.stringify(FORMATIONS["4-4-2"])));
      // Offer to resume a lineup saved on this device (see Save button).
      const saved = loadLocalLineup(LINEUP_STORAGE_KEY);
      if (saved) setResumeData(saved);
    }
  }, [code]);

  const handleSaveLocal = () => {
    const ok = saveLocalLineup(LINEUP_STORAGE_KEY, { formation, players: lineupPlayers, teamKitUrl });
    if (!ok) {
      alert("Couldn't save — your browser's storage is full. Try removing the Team Kit image and saving again.");
      return;
    }
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  const resumeSavedLineup = () => {
    if (!resumeData) return;
    setFormation(resumeData.formation || "4-4-2");
    setLineupPlayers(resumeData.players || []);
    setTeamKitUrl(resumeData.teamKitUrl || "");
    setResumeData(null);
  };

  // Handle formation change
  const handleFormationChange = (e) => {
    const f = e.target.value;
    setFormation(f);
    // Overwrite players coordinates, but preserve picked player names if any
    const template = JSON.parse(JSON.stringify(FORMATIONS[f]));
    template.forEach((item, idx) => {
      const current = lineupPlayers[idx];
      if (current && current.name) {
        item.name = current.name;
        item.number = current.number;
      }
    });
    setLineupPlayers(template);
  };

  const assignPlayer = (slotIdx, player) => {
    setLineupPlayers((prev) => {
      const copy = [...prev];
      copy[slotIdx] = {
        ...copy[slotIdx],
        name: player.name,
        number: player.number,
        player_id: player.id,
      };
      return copy;
    });
    setActiveSlotIdx(null);
  };

  // Opening a slot's dropdown pre-fills the custom-name draft with
  // whatever's already there (only when it's actually a custom name —
  // i.e. no player_id — so a squad pick's name isn't offered back as
  // an editable draft by accident).
  const toggleSlot = (idx) => {
    const opening = activeSlotIdx !== idx;
    setActiveSlotIdx(opening ? idx : null);
    if (opening) {
      const current = lineupPlayers[idx];
      setCustomNameDraft(current && !current.player_id && current.name ? current.name : "");
    }
  };

  // Custom name is mutually exclusive with picking from the squad —
  // typing one clears any player_id reference on that slot.
  const applyCustomName = (idx) => {
    const trimmed = customNameDraft.trim();
    if (!trimmed) return;
    setLineupPlayers((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], name: trimmed, player_id: null };
      return copy;
    });
    setActiveSlotIdx(null);
  };

  // Team Kit — a single bulk shirt image for the toolbar, applied to
  // every outfield slot at once. Read entirely client-side (see
  // lib/customShirtUpload.js), never uploaded anywhere.
  const handleTeamKitUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setTeamKitError("");
    try {
      const dataUrl = await readShirtImageFile(file);
      setTeamKitUrl(dataUrl);
    } catch (err) {
      setTeamKitError(err.message || "Couldn't use that image.");
    }
  };

  const removeTeamKit = () => {
    setTeamKitUrl("");
    setTeamKitError("");
  };

  const handleReset = () => {
    if (window.confirm("Reset your lineup board?")) {
      setLineupPlayers(JSON.parse(JSON.stringify(FORMATIONS[formation])));
      // Also drop back to the default shirt — clears any fan-uploaded
      // Team Kit so every outfield slot returns to the plain default
      // shirt, same as a fresh visit.
      removeTeamKit();
    }
  };

  // Drag and drop tracking
  const handleDragStart = (idx) => (e) => {
    if (isViewOnly) return;
    setDraggingIdx(idx);
    e.preventDefault();
  };

  const handleTouchStart = (idx) => (e) => {
    if (isViewOnly) return;
    setDraggingIdx(idx);
  };

  useEffect(() => {
    if (draggingIdx === null) return undefined;

    const onMove = (e) => {
      if (!pitchRef.current) return;
      const isTouch = !!e.touches;
      // Stop the page itself from scrolling while a shirt is being dragged
      // on mobile — without this, touchmove bubbles up to the browser's
      // native scroll handler and the whole page slides instead of the shirt.
      if (isTouch) e.preventDefault();
      const rect = pitchRef.current.getBoundingClientRect();
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;

      const rawX = clientX - rect.left;
      const rawY = clientY - rect.top;
      
      // Convert to percent coordinates
      let pctX = Math.round((rawX / rect.width) * 100);
      let pctY = Math.round((rawY / rect.height) * 100);

      // Clamp so players do not fly off-pitch
      pctX = Math.min(94, Math.max(6, pctX));
      pctY = Math.min(92, Math.max(8, pctY));

      setLineupPlayers((prev) => {
        const copy = [...prev];
        copy[draggingIdx] = { ...copy[draggingIdx], x: pctX, y: pctY };
        return copy;
      });
    };

    const onUp = () => {
      setDraggingIdx(null);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
  }, [draggingIdx]);

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  // Converts a "data:image/jpeg;base64,...." string straight into a Blob
  // via atob/Uint8Array — deliberately NOT using fetch(dataUrl).blob(),
  // which is known to silently fail on some mobile Chrome/WebView builds
  // for large data: URIs and was the real reason Instagram sharing (which
  // needs a File) kept failing on-device.
  const dataUrlToBlob = (dataUrl) => {
    const [header, base64] = dataUrl.split(",");
    const mimeMatch = /data:(.*?);base64/.exec(header || "");
    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  // Bakes the brand logo + bold watermark text onto the raw pitch snapshot.
  // Both are now rendered OUTSIDE the interactive pitch (to avoid overlapping
  // shirts on mobile), so they no longer show up in the toJpeg() capture —
  // this composites them back in for the exported/shared image only. The
  // watermark gets its own footer strip BELOW the pitch pixels so it can
  // never overlap a shirt or player name.
  const composeShareImage = async (pitchDataUrl) => {
    const pitchImg = await loadImage(pitchDataUrl);
    const footerH = Math.round(pitchImg.height * 0.09);
    const canvas = document.createElement("canvas");
    canvas.width = pitchImg.width;
    canvas.height = pitchImg.height + footerH;
    const ctx = canvas.getContext("2d");

    // Footer strip background matches the pitch so the seam is invisible
    ctx.fillStyle = "#052e22";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(pitchImg, 0, 0);

    try {
      const lookup = await api.get("/media/lookup", { params: { filename: "2tonelogosmall" } });
      const logoImg = await loadImage(resolveAsset(lookup.data.url));
      const logoW = canvas.width * 0.26;
      const logoH = logoW * (logoImg.height / logoImg.width);
      const pad = canvas.width * 0.03;
      ctx.globalAlpha = 1;
      ctx.drawImage(logoImg, canvas.width - logoW - pad, pad, logoW, logoH);
    } catch (err) {
      console.error("Could not load LAST logo for share image:", err);
    }

    // Bold watermark centred inside the dedicated footer strip — always
    // below the pitch pixels, so it can never sit over a shirt/name.
    const text = "Build your own City XI at letsallsingtogether.com";
    const fontSize = Math.max(16, Math.round(canvas.width * 0.028));
    ctx.font = `900 ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const tx = canvas.width / 2;
    const ty = pitchImg.height + footerH / 2;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(text, tx, ty);

    return canvas.toDataURL("image/jpeg", 0.85);
  };

  // Fixed portrait export size (CSS px, matches a typical mobile pitch's
  // on-screen 3:4 box) — used to force the real pitch element's width down
  // before capture (see forceExportLayout / withExportLayout above), so the
  // browser genuinely reflows the layout into a portrait shape instead of
  // capturing the wide desktop layout cropped/squashed into a portrait canvas.
  const EXPORT_W = 390;
  const EXPORT_H = Math.round((EXPORT_W * 4) / 3);
  // Generates a fresh composited pitch JPEG on demand — used by "Post to
  // Match Comments" when the fan hasn't already generated one via Share.
  const generatePitchImage = async () => {
    if (!pitchRef.current) return null;
    const rawDataUrl = await withExportLayout(() =>
      toJpeg(pitchRef.current, {
        quality: 0.9,
        pixelRatio: 1.5,
        backgroundColor: "#064e3b",
      })
    );
    return composeShareImage(rawDataUrl);
  };

  // Standalone "Download Image" toolbar button — independent of the Share
  // flow below (no share_code / backend round-trip needed), so a fan can
  // save their lineup as an image in one click instead of relying on
  // screenshots.
  const [downloadingImage, setDownloadingImage] = useState(false);
  const handleDownloadLineup = async () => {
    setDownloadingImage(true);
    try {
      const dataUrl = await generatePitchImage();
      if (!dataUrl) return;
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `last-lineup-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Image saved!", { description: "Check your downloads folder." });
    } catch (err) {
      console.error("Failed to download lineup image:", err);
      alert("Couldn't generate the image. Please try again.");
    } finally {
      setDownloadingImage(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    setGeneratingImg(true);
    setGeneratedImage("");
    setImageReady(false);
    setShareFile(null);
    try {
      const r = await api.post("/lineup-builder/share", {
        formation,
        players: lineupPlayers,
      });
      const code = r.data.share_code;
      setShareCode(code);
      
      // Delay slightly to let the active modal or states paint, then render the JPEG of the board
      setTimeout(async () => {
        if (pitchRef.current) {
          try {
            const rawDataUrl = await withExportLayout(() =>
              toJpeg(pitchRef.current, {
                quality: 0.9,
                pixelRatio: 1.5,
                backgroundColor: "#064e3b", // emerald pitch
              })
            );
            const finalDataUrl = await composeShareImage(rawDataUrl);
            setGeneratedImage(finalDataUrl);

            // Pre-build the File object now (not inside the Instagram button's
            // click handler) so tapping Instagram can call navigator.share()
            // synchronously — awaiting a fetch/blob first can lose the
            // "user activation" some mobile browsers require to allow it.
            try {
              const blob = dataUrlToBlob(finalDataUrl);
              setShareFile(new File([blob], `last-lineup-${code}.jpg`, { type: "image/jpeg" }));
            } catch (fileErr) {
              console.error("Could not prepare share file:", fileErr);
            }

            // Persist the rendered JPEG server-side so the share link's
            // og:image is the fan's actual lineup, not the site default —
            // that's what X/Facebook/WhatsApp preview when the link is shared.
            // The X/FB/WhatsApp/Instagram buttons stay disabled (imageReady
            // stays false) until this succeeds, so nobody shares the link
            // before the crawler has an image to find.
            try {
              await api.post(`/lineup-builder/${code}/image`, { image: finalDataUrl });
              setImageReady(true);
            } catch (uploadErr) {
              console.error("Failed to save share image server-side:", uploadErr);
              alert("Couldn't save the lineup image for link previews — you can still copy the link or download the JPEG.");
            }
          } catch (err) {
            console.error("Failed to generate lineup image:", err);
          } finally {
            setGeneratingImg(false);
          }
        } else {
          setGeneratingImg(false);
        }
      }, 400);

    } catch (_) {
      alert("Failed to share lineup. Please try again.");
      setGeneratingImg(false);
    } finally {
      setSharing(false);
    }
  };

  const shareUrl = `${window.location.origin}/lineup-builder/${shareCode}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadImage = () => {
    if (!shareFile && !generatedImage) return;
    const blobUrl = shareFile ? URL.createObjectURL(shareFile) : generatedImage;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `last-lineup-${shareCode || "card"}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (shareFile) setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    toast.success("Image saved!", { description: "Check your downloads folder." });
  };

  const downloadImageFallback = () => {
    downloadImage();
  };

  // Whether the device supports the native OS share sheet at all — used to
  // decide whether to show the generic "Share" button (Messages, Mail,
  // WhatsApp, Instagram, etc. all show up as targets on mobile). Desktop
  // browsers mostly don't support this, so the button simply isn't shown
  // there — Download/X/Facebook/WhatsApp cover that case already.
  const nativeShareSupported = typeof navigator !== "undefined" && !!navigator.share;

  // One-tap native share sheet — attaches the lineup image itself (so
  // recipients always see the actual card, regardless of whether a given
  // app renders link previews) plus a caption with the share link.
  const handleNativeShare = () => {
    if (!shareFile) {
      alert("The image is still generating — please wait a moment and try again.");
      return;
    }
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [shareFile] })) {
      navigator.share({
        files: [shareFile],
        title: "My Coventry City Matchday 11",
        text: "Check out my Coventry City Matchday 11! Build yours at letsallsingtogether.com",
      }).catch((err) => {
        if (err && err.name === "AbortError") return; // user cancelled the native share sheet
        console.error("Native share failed:", err);
        downloadImageFallback();
      });
      return;
    }
    downloadImageFallback();
  };

  // Instagram has no web share-intent URL, so we lean on the native OS
  // share sheet (which lists Instagram among the app targets) when the
  // browser supports sharing files. `shareFile` is pre-built ahead of time
  // (see handleShare) so this call stays synchronous from the click —
  // no `await` before `navigator.share()` — which some mobile browsers
  // require to keep the "user activation" that permission needs.
  const handleInstagramShare = () => {
    if (!shareFile) {
      alert("The image is still generating — please wait a moment and try again.");
      return;
    }
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [shareFile] })) {
      navigator.share({
        files: [shareFile],
        title: "My Coventry City Matchday 11",
        text: "Check out my Coventry City Matchday 11! Build yours at letsallsingtogether.com",
      }).catch((err) => {
        if (err && err.name === "AbortError") return; // user cancelled the native share sheet
        console.error("Native share to Instagram failed:", err);
        downloadImageFallback();
      });
      return;
    }
    downloadImageFallback();
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-lineup-builder">
      {/* Fetch-free OG tags for the share view — set synchronously on
          first render from the URL's `code` alone (title/description are
          generic, the image is a backend redirect keyed off `code`), so
          they exist before the /lineup-builder/{code} data fetch below
          even starts, let alone resolves. */}
      {code ? (
        <PostHelmet
          title="My Coventry City Matchday 11 — LAST"
          description="Check out this Coventry City lineup, built with the LAST Lineup Builder. Build your own at letsallsingtogether.com"
          image={`/api/lineup-builder/${code}/og-image`}
          url={`${window.location.origin}/lineup-builder/${code}`}
          type="website"
        />
      ) : null}
      <Header />

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        {/* Header toolbar */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-black/10 pb-6">
          <div>
            <div className="flex items-center gap-1.5 text-brand-accent" style={kickerStyle(copy)}>
              <span className="label-kicker">{copy.kicker}</span>
            </div>
            <h2 className="brand-headline mt-1 font-display text-3xl font-black tracking-tight text-[var(--brand-ink)] sm:text-4xl" style={titleStyle(copy)}>
              {copy.title}
            </h2>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              {isViewOnly ? "Viewing a shared lineup. Click Edit/Copy to build yours!" : copy.intro}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
            {!isViewOnly ? (
              <>
                <select
                  value={formation}
                  onChange={handleFormationChange}
                  className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm font-semibold outline-none focus:border-[#0A0F17]"
                  data-testid="lineup-formation-select"
                >
                  {Object.keys(FORMATIONS).map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <label
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-black/20 bg-white px-3 text-sm font-semibold text-[var(--brand-body)] hover:border-[#6CABDD] hover:text-[#002d75] cursor-pointer"
                  data-testid="lineup-team-kit-upload-label"
                  title="Replace every outfield shirt with your own kit image"
                >
                  <Upload size={14} />
                  {teamKitUrl ? "Change Kit" : "Team Kit"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleTeamKitUpload}
                    className="hidden"
                    data-testid="lineup-team-kit-upload-input"
                  />
                </label>
                {teamKitUrl ? (
                  <button
                    type="button"
                    onClick={removeTeamKit}
                    className="inline-flex h-9 items-center rounded-md border border-black/10 bg-white px-2.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                    data-testid="lineup-team-kit-remove"
                    title="Remove Team Kit"
                  >
                    <XIcon size={14} />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex h-9 items-center gap-1 rounded-md border border-black/10 bg-white px-3 text-sm font-semibold text-[var(--brand-ink)] hover:bg-black/5"
                  data-testid="lineup-reset"
                >
                  <RotateCcw size={14} /> Reset
                </button>
                <button
                  type="button"
                  onClick={handleSaveLocal}
                  className="inline-flex h-9 items-center gap-1 rounded-md border border-black/10 bg-white px-3 text-sm font-semibold text-[var(--brand-ink)] hover:bg-black/5"
                  data-testid="lineup-save-local"
                >
                  {savedFlash ? <Check size={14} className="text-emerald-600" /> : <SaveIcon size={14} />}
                  {savedFlash ? "Saved!" : "Remember My Lineup"}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadLineup}
                  disabled={downloadingImage}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-600 px-4 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
                  data-testid="lineup-download-image"
                  title="Download your lineup as an image"
                >
                  {downloadingImage ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {downloadingImage ? "Generating..." : "Download Image"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 text-sm font-bold text-white hover:bg-black"
                  data-testid="lineup-share"
                >
                  <Share2 size={14} /> Share Lineup
                </button>
                <button
                  type="button"
                  onClick={() => setShowPostModal(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-black/10 bg-white px-4 text-sm font-bold text-[var(--brand-ink)] hover:bg-black/5"
                  data-testid="lineup-post-to-comments"
                >
                  <MessageCircle size={14} /> Post to Match Preview Comments
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/lineup-builder")}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0A0F17] px-5 text-sm font-bold text-white hover:bg-black"
                data-testid="lineup-edit-copy"
              >
                Edit / Copy Lineup
              </button>
            )}
          </div>
        </div>

        {teamKitError ? (
          <p className="mb-4 -mt-2 text-xs font-semibold text-red-600" data-testid="lineup-team-kit-error">
            {teamKitError}
          </p>
        ) : null}

        {/* Resume-saved-lineup banner — only when a save exists on this device */}
        {resumeData && !isViewOnly ? (
          <div
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#6CABDD]/30 bg-[#6CABDD]/10 px-4 py-3"
            data-testid="lineup-resume-banner"
          >
            <p className="text-sm font-semibold text-[var(--brand-ink)]">
              You have a saved lineup from last time — want to pick up where you left off?
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resumeSavedLineup}
                className="inline-flex h-8 items-center gap-1 rounded-md bg-[#0A0F17] px-3 text-xs font-bold text-white hover:bg-black"
                data-testid="lineup-resume-btn"
              >
                Resume
              </button>
              <button
                type="button"
                onClick={() => setResumeData(null)}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 bg-white px-3 text-xs font-semibold text-[var(--brand-ink)] hover:bg-black/5"
                data-testid="lineup-dismiss-resume-btn"
              >
                <XIcon size={12} /> Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {/* Share Modal Dialog */}
        {shareCode ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="lineup-share-modal">
            <div className="w-full max-w-lg rounded-md bg-white p-6 shadow-xl animate-scale-up max-h-[90vh] overflow-y-auto scrollbar-thin">
              <h3 className="font-display text-lg font-black text-[var(--brand-ink)] flex items-center gap-1.5 border-b border-black/5 pb-2">
                <Check size={18} className="text-emerald-500" />
                Lineup shared!
              </h3>
              
              {/* Image Generator / Preview block */}
              <div className="mt-4">
                <p className="label-kicker text-[var(--brand-body)]">My Lineup Card</p>
                {generatingImg ? (
                  <div className="mt-2 flex flex-col items-center justify-center rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-10 h-60">
                    <Loader2 size={24} className="animate-spin text-[#6CABDD] mb-2" />
                    <span className="text-xs font-semibold text-[var(--brand-body)]">Generating JPEG card...</span>
                  </div>
                ) : generatedImage ? (
                  <div className="mt-2 space-y-3">
                    <div className="overflow-hidden rounded-md border border-black/10 shadow-sm max-w-xs mx-auto">
                      <img src={generatedImage} alt="Shared Lineup Card" className="w-full h-auto object-contain" />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      {nativeShareSupported ? (
                        <button
                          type="button"
                          onClick={handleNativeShare}
                          className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0A0F17] px-4 text-sm font-bold text-white hover:bg-black transition sm:w-auto"
                          data-testid="lineup-share-native"
                        >
                          <Share2 size={14} /> Share
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={downloadImage}
                        className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-600 px-4 text-xs font-bold text-white hover:bg-emerald-700 transition"
                        data-testid="lineup-download-jpeg"
                      >
                        <Download size={12} /> Download JPEG Image
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Share URL copy */}
              <div className="mt-5">
                <p className="label-kicker text-[var(--brand-body)]">Share Link</p>
                <div className="mt-1 flex items-center gap-2 rounded-md border border-black/15 bg-[#F8FAFC] px-3 py-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    className="block w-full bg-transparent font-mono text-xs text-[var(--brand-ink)] outline-none"
                  />
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="flex h-7 w-7 items-center justify-center rounded-sm bg-white border border-black/10 hover:border-[#0A0F17]"
                    title="Copy share link"
                  >
                    {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                  </button>
                </div>
              </div>

              {/* Social Sharing Buttons Row — only needed as a fallback
                  on desktop browsers that don't support the native OS
                  share sheet above (which already covers X/Facebook/
                  WhatsApp/Instagram as targets on mobile). */}
              {!nativeShareSupported ? (
                <div className="mt-5 border-t border-black/5 pt-4">
                  <p className="label-kicker text-[var(--brand-body)] mb-2">
                    Share directly to:
                    {!imageReady ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-[var(--brand-body)]" data-testid="lineup-share-preparing">
                        <Loader2 size={10} className="animate-spin" /> preparing shareable image…
                      </span>
                    ) : null}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!imageReady}
                      onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent("Check out my Coventry City Matchday 11! Build yours at letsallsingtogether.com")}`, "_blank", "noopener,noreferrer")}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
                      data-testid="lineup-share-twitter"
                    >
                      <Twitter size={12} /> X (Twitter)
                    </button>
                    <button
                      type="button"
                      disabled={!imageReady}
                      onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer")}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#1877F2] px-3 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
                      data-testid="lineup-share-facebook"
                    >
                      <Facebook size={12} /> Facebook
                    </button>
                    <button
                      type="button"
                      disabled={!imageReady}
                      onClick={() => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out my Coventry City Matchday 11! Build yours at: ${shareUrl}`)}`, "_blank", "noopener,noreferrer")}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#25D366] px-3 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
                      data-testid="lineup-share-whatsapp"
                    >
                      <MessageSquare size={12} /> WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={handleInstagramShare}
                      disabled={!shareFile}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] px-3 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
                      data-testid="lineup-share-instagram"
                    >
                      <Instagram size={12} /> Instagram
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="mt-6 flex justify-end border-t border-black/5 pt-4">
                <button
                  type="button"
                  onClick={() => setShareCode("")}
                  className="rounded-md bg-[#0A0F17] px-4 py-2 font-display text-xs font-semibold text-white hover:opacity-90"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Board content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* Main Football Pitch Graphic */}
          <div className="lg:col-span-3">
            <div
              ref={pitchRef}
              className="relative w-full overflow-hidden rounded-md border-2 border-white bg-gradient-to-b from-emerald-800 to-emerald-950 shadow-lg select-none"
              style={{
                aspectRatio: "3 / 4",
                // Shrinks to fit whatever vertical space is actually left
                // below the sticky header on shorter screens (laptops,
                // landscape tablets), instead of always rendering up to
                // 720px tall — on a short viewport that forced the user to
                // scroll past the pitch, and every scroll position had some
                // slice of it hidden behind the sticky header. Still caps
                // at 720px on tall screens, same as before.
                // `minHeight` is raised just enough (520px, from a prior
                // 320px) so the GK slot's shirt + name/"Select..." tag —
                // which sit at a FIXED y% of the pitch but have a fixed
                // pixel size that doesn't shrink with it — always stays
                // fully inside the pitch box instead of being clipped by
                // `overflow-hidden` on short/shrunk pitches; does not
                // change the cap or scaling formula on normal/tall screens
                // (calc/720 branch is untouched, so this only raises the
                // floor on the shortest windows).
                maxHeight: forceExportLayout ? "none" : "min(720px, calc(100vh - 340px))",
                minHeight: forceExportLayout ? "0" : "520px",
                ...(forceExportLayout
                  ? { width: `${EXPORT_W}px`, maxWidth: `${EXPORT_W}px` }
                  : {}),
              }}
              data-testid="lineup-pitch"
              onClick={(e) => {
                if (
                  activeSlotIdx !== null &&
                  !e.target.closest('[data-testid^="lineup-slot-"]') &&
                  !e.target.closest('[data-testid="lineup-player-dropdown"]')
                ) {
                  setActiveSlotIdx(null);
                }
              }}
            >
              {/* Pitch Marking Lines (SVG Overlay) */}
              <svg viewBox="0 0 300 400" className="absolute inset-0 h-full w-full pointer-events-none opacity-25" preserveAspectRatio="none">
                {/* Grass boundary border */}
                <rect x="0" y="0" width="300" height="400" fill="none" stroke="#FFF" strokeWidth="1.5" />
                
                {/* Center Line & Spot */}
                <line x1="0" y1="200" x2="300" y2="200" stroke="#FFF" strokeWidth="1.5" />
                <circle cx="150" cy="200" r="2.5" fill="#FFF" />
                
                {/* Center Circle */}
                <circle cx="150" cy="200" r="40" fill="none" stroke="#FFF" strokeWidth="1.5" />
                
                {/* TOP HALF MARKINGS */}
                {/* 18-yard Penalty Box */}
                <rect x="60" y="0" width="180" height="66" fill="none" stroke="#FFF" strokeWidth="1.5" />
                {/* 6-yard Goal Box */}
                <rect x="110" y="0" width="80" height="22" fill="none" stroke="#FFF" strokeWidth="1.5" />
                {/* Penalty Spot */}
                <circle cx="150" cy="44" r="2" fill="#FFF" />
                {/* Penalty Arc */}
                <path d="M120,66 A30,30 0 0,0 180,66" fill="none" stroke="#FFF" strokeWidth="1.5" />
                {/* Goal Frame Netting */}
                <rect x="125" y="-8" width="50" height="8" fill="#FFFFFF1A" stroke="#FFF" strokeWidth="1" />
                <line x1="125" y1="-8" x2="135" y2="0" stroke="#FFFFFF4D" strokeWidth="0.8" />
                <line x1="142" y1="-8" x2="148" y2="0" stroke="#FFFFFF4D" strokeWidth="0.8" />
                <line x1="158" y1="-8" x2="152" y2="0" stroke="#FFFFFF4D" strokeWidth="0.8" />
                <line x1="175" y1="-8" x2="165" y2="0" stroke="#FFFFFF4D" strokeWidth="0.8" />

                {/* BOTTOM HALF MARKINGS */}
                {/* 18-yard Penalty Box */}
                <rect x="60" y="334" width="180" height="66" fill="none" stroke="#FFF" strokeWidth="1.5" />
                {/* 6-yard Goal Box */}
                <rect x="110" y="378" width="80" height="22" fill="none" stroke="#FFF" strokeWidth="1.5" />
                {/* Penalty Spot */}
                <circle cx="150" cy="356" r="2" fill="#FFF" />
                {/* Penalty Arc */}
                <path d="M120,334 A30,30 0 0,1 180,334" fill="none" stroke="#FFF" strokeWidth="1.5" />
                {/* Goal Frame Netting */}
                <rect x="125" y="400" width="50" height="8" fill="#FFFFFF1A" stroke="#FFF" strokeWidth="1" />
                <line x1="125" y1="408" x2="135" y2="400" stroke="#FFFFFF4D" strokeWidth="0.8" />
                <line x1="142" y1="408" x2="148" y2="400" stroke="#FFFFFF4D" strokeWidth="0.8" />
                <line x1="158" y1="408" x2="152" y2="400" stroke="#FFFFFF4D" strokeWidth="0.8" />
                <line x1="175" y1="408" x2="165" y2="400" stroke="#FFFFFF4D" strokeWidth="0.8" />
              </svg>

              {/* Position Slots / Player shirts */}
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/15 backdrop-blur-xs">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/25 border-t-white" />
                    <span className="text-sm font-bold text-white uppercase tracking-wider">Loading Lineup...</span>
                  </div>
                </div>
              ) : (
                lineupPlayers.map((p, idx) => {
                  const isGK = p.is_gk || p.role === "GK";
                  const leftPos = `${p.x}%`;
                  const topPos = `${p.y}%`;
                  const active = activeSlotIdx === idx;

                  return (
                    <div
                      key={`${p.role}-${idx}`}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group ${active ? "z-40" : "z-10"}`}
                      style={{ left: leftPos, top: topPos }}
                    >
                      {/* Shirt trigger — directly draggable by clicking/mouse dragging, or touching/touch dragging */}
                      <button
                        type="button"
                        onClick={() => { if (!isViewOnly && draggingIdx === null) toggleSlot(idx); }}
                        onMouseDown={handleDragStart(idx)}
                        onTouchStart={handleTouchStart(idx)}
                        disabled={isViewOnly}
                        style={{ touchAction: "none" }}
                        className={`focus:outline-none rounded-md p-1 transition cursor-move flex flex-col items-center select-none ${
                          active ? "bg-white/20 scale-105 ring-2 ring-[#6CABDD]" : "hover:scale-105"
                        }`}
                        data-testid={`lineup-slot-${p.role}`}
                      >
                        <ShirtIcon
                          isGk={isGK}
                          number={p.number}
                          sizeClass={shirtSizeClass}
                          design={!isGK && teamKitUrl ? { pattern_type: "custom_image", image_url: teamKitUrl } : undefined}
                        />
                        <span className="mt-1.5 whitespace-nowrap font-display text-xs sm:text-[13px] font-black tracking-tight leading-none bg-[#002d75] text-white px-2.5 py-1.5 rounded-sm border border-white/25 shadow-md">
                          {p.name ? getSurname(p.name) : "Select..."}
                        </span>
                      </button>

                      {/* Dropdown menu to pick player — flips vertically when
                          the slot sits near the bottom of the pitch (e.g. the
                          GK) and horizontally when near the left/right edge
                          (e.g. full-backs/wingers) so it's never clipped by
                          the viewport edge in either direction. */}
                      {active ? (
                        <div
                          className={`absolute bg-white border border-black/15 shadow-xl rounded-md w-56 max-h-80 overflow-y-auto scrollbar-thin z-30 py-1 ${
                            p.y > 60 ? "bottom-16" : "top-16"
                          } ${
                            p.x < 25 ? "left-0" : p.x > 75 ? "right-0" : "left-1/2 -translate-x-1/2"
                          }`}
                          data-testid="lineup-player-dropdown"
                        >
                          <p className="px-3 py-1 label-kicker text-[var(--brand-body)] border-b border-black/5">Choose player:</p>
                          {squad.map((sp) => (
                            <button
                              key={sp.id}
                              type="button"
                              onClick={() => assignPlayer(idx, sp)}
                              className="w-full text-left px-3 py-1.5 text-xs font-semibold text-[var(--brand-ink)] hover:bg-[#6CABDD]/15 flex items-center justify-between"
                            >
                              <span>{sp.name}</span>
                              <span className="font-display font-black text-[var(--brand-body)]">#{sp.number}</span>
                            </button>
                          ))}

                          {/* Custom name — mutually exclusive with picking
                              from the squad above; typing + applying here
                              clears any player_id on this slot. */}
                          <div className="border-t border-black/10 mt-1 px-3 py-2 space-y-1.5">
                            <p className="label-kicker text-[var(--brand-body)]">Or type a custom name</p>
                            <div className="flex gap-1.5">
                              <input
                                type="text"
                                maxLength={24}
                                placeholder="Custom name"
                                value={customNameDraft}
                                onChange={(e) => setCustomNameDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") applyCustomName(idx); }}
                                data-testid="lineup-custom-name-input"
                                className="flex-1 min-w-0 rounded border border-black/15 px-2 py-1 text-xs"
                              />
                              <button
                                type="button"
                                onClick={() => applyCustomName(idx)}
                                data-testid="lineup-custom-name-apply"
                                className="shrink-0 rounded bg-[#002d75] px-2.5 py-1 text-xs font-bold text-white hover:bg-[#00214f]"
                              >
                                Use
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>

            {/* Watermark — sits below the pitch entirely so it never overlaps
                a shirt on mobile; still baked into the exported share JPEG. */}
            <p
              className="mt-3 text-center font-display text-xs font-black uppercase tracking-widest text-[var(--brand-body)]"
              style={{ fontWeight: 900 }}
              data-testid="lineup-watermark-text"
            >
              Build your own City XI at letsallsingtogether.com
            </p>
          </div>

          {/* Quick squad list reference & formations helper */}
          <div className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
            <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2 border-b border-black/5 pb-2">
              <User size={16} className="text-[#6CABDD]" />
              Coventry Squad
            </h3>
            <div className="space-y-2 overflow-y-auto max-h-[500px] scrollbar-thin">
              {squad.map((sp) => (
                <div key={sp.id} className="flex items-center justify-between border-b border-black/5 py-1 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-[var(--brand-ink)]">
                    <span className={`h-2 w-2 rounded-full ${sp.position_type === "goalkeeper" ? "bg-emerald-500" : "bg-sky-400"}`} />
                    <span>{sp.name}</span>
                  </div>
                  <span className="font-display font-black text-[var(--brand-body)]">#{sp.number}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {showPostModal ? (
        <PostToCommentsModal
          onClose={() => setShowPostModal(false)}
          generatedImage={generatedImage}
          generatePitchImage={generatePitchImage}
        />
      ) : null}

      <Footer />
    </div>
  );
}

